import { memo } from "react";
import AdminBreadcrumb from "@/features/admin/components/AdminBreadcrumb";
import TeamNavigation from "../TeamManagement/TeamNavigation";
import TeamFilter from "../TeamManagement/TeamFilter";
import Navbar from "@/components/Common/Navbar";
import { Button } from "@/components/ui/button";
import type { SortOption } from "@/features/auth/types/hashKeys.types";

interface HashKeysErrorStateProps {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  sortBy?: SortOption;
  setSortBy?: (sort: SortOption) => void;
  onRetry?: () => void;
  filterRole?: string; // Add this
  onFilterChange?: (role: string) => void; // Add this
}

/**
 * Error state component for hash keys management
 */
const HashKeysErrorState = memo<HashKeysErrorStateProps>(
  ({
    searchQuery,
    setSearchQuery,
    sortBy = "alphabetical_asc",
    setSortBy,
    onRetry,
    filterRole = "all", // Add default
    onFilterChange, // Add this
  }) => (
    <main className="scrollbar-thin flex h-screen min-h-0 flex-1 flex-col gap-7 overflow-y-auto p-3 px-4 pr-0 pl-6 transition-all duration-300">
      <div className="mb-5 flex-shrink-0">
        <Navbar subtitle="manager" />
      </div>

      {/* Fixed Header */}
      <div className="flex-shrink-0 px-4 transition-all duration-300 md:px-16 lg:px-[120px]">
        <div className="flex md:flex-col md:gap-4 lg:flex-row lg:items-center lg:justify-between">
          <AdminBreadcrumb title="Team Management" />
          <TeamNavigation
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
          />
        </div>
      </div>

      <div className="min-h-0 flex-1 px-4 transition-all duration-300 md:px-16 lg:px-[120px]">
        <div className="flex min-h-0 w-full flex-row justify-start gap-8 pb-20">
          <div className="flex w-full justify-between gap-8 md:flex-col-reverse lg:flex-col-reverse xl:flex-row">
            {/* Empty action buttons area */}
            <div className="flex w-auto flex-row gap-2">
              <div className="h-[52px] w-32" />
            </div>

            {/* Error message in main content area */}
            <div className="flex w-full items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-white p-12">
              <div className="text-center">
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-50">
                  <i className="ri-error-warning-line text-3xl text-red-500"></i>
                </div>
                <h3 className="mb-2 text-lg font-bold text-neutral-800">
                  Failed to Load Hash Keys
                </h3>
                <p className="mb-4 text-sm font-medium text-neutral-600">
                  We encountered an error while loading your hash keys.
                </p>
                {onRetry && (
                  <Button onClick={onRetry} variant="default" className="mt-2">
                    <i className="ri-refresh-line mr-2"></i>
                    Retry
                  </Button>
                )}
              </div>
            </div>

            {/* Filter sidebar */}
            {setSortBy && (
              <TeamFilter
                sortBy={sortBy}
                onSortChange={(value: string) => setSortBy(value as SortOption)}
                filterRole={filterRole}
                onFilterChange={onFilterChange || (() => {})} // Use the prop or fallback
                roleCounts={{}}
                totalMembers={0}
              />
            )}
          </div>
        </div>
      </div>
    </main>
  ),
);

HashKeysErrorState.displayName = "HashKeysErrorState";

export default HashKeysErrorState;
